/**
 * @fileoverview Componente responsável por configurar horários e janelas de atendimento.
 */

import React, { useState, useEffect, useMemo } from "react";
import { makeStyles, Chip, IconButton, Typography, Tooltip, Switch } from "@material-ui/core";
import TextField from "@material-ui/core/TextField";
import NumberFormat from "react-number-format";
import ButtonWithSpinner from "../ButtonWithSpinner";
import { i18n } from "../../translate/i18n";

import { Plus, Trash2 } from "lucide-react";
const ALL_DAYS = [
  { weekday: "Segunda-feira", weekdayEn: "monday",    short: "Seg" },
  { weekday: "Terça-feira",   weekdayEn: "tuesday",   short: "Ter" },
  { weekday: "Quarta-feira",  weekdayEn: "wednesday", short: "Qua" },
  { weekday: "Quinta-feira",  weekdayEn: "thursday",  short: "Qui" },
  { weekday: "Sexta-feira",   weekdayEn: "friday",    short: "Sex" },
  { weekday: "Sábado",        weekdayEn: "saturday",  short: "Sáb" },
  { weekday: "Domingo",       weekdayEn: "sunday",    short: "Dom" },
];

const WEEKDAYS = ["monday", "tuesday", "wednesday", "thursday", "friday"];
const ALL_WEEKDAYS_EN = ALL_DAYS.map(d => d.weekdayEn);

const TIME_RE = /^([01]\d|2[0-3]):([0-5]\d)$/;

const toMinutes = (hhmm) => {
  if (!TIME_RE.test(hhmm)) return null;
  const [h, m] = hhmm.split(":").map(Number);
  return h * 60 + m;
};

const makeId = () => `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

const useStyles = makeStyles((theme) => ({
  root: {
    width: "100%",
    padding: theme.spacing(1),
    fontFamily: "'Montserrat', sans-serif",
    "& *": {
      fontFamily: "'Montserrat', sans-serif",
    },
  },
  groupCard: {
    borderRadius: 14,
    padding: theme.spacing(2, 2.5),
    marginBottom: theme.spacing(2),
    background: theme.palette.type === "dark"
      ? "rgba(255, 255, 255, 0.04)"
      : "rgba(0, 0, 0, 0.025)",
    border: theme.palette.type === "dark"
      ? "1px solid rgba(255, 255, 255, 0.1)"
      : "1px solid rgba(0, 0, 0, 0.08)",
  },
  groupCardClosed: {
    background: theme.palette.type === "dark"
      ? "rgba(239, 68, 68, 0.05)"
      : "rgba(239, 68, 68, 0.03)",
    border: theme.palette.type === "dark"
      ? "1px solid rgba(239, 68, 68, 0.2)"
      : "1px solid rgba(239, 68, 68, 0.15)",
  },
  groupHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: theme.spacing(1),
    gap: theme.spacing(1),
  },
  closedToggle: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(0.5),
  },
  closedLabel: {
    fontSize: "0.8rem",
    color: theme.palette.type === "dark" ? "rgba(255,255,255,0.65)" : "rgba(0,0,0,0.6)",
    fontWeight: 500,
  },
  quickActions: {
    display: "flex",
    gap: theme.spacing(0.75),
    marginBottom: theme.spacing(1),
    flexWrap: "wrap",
  },
  quickActionBtn: {
    border: "none",
    cursor: "pointer",
    padding: "4px 10px",
    borderRadius: 8,
    fontSize: "0.72rem",
    fontWeight: 600,
    fontFamily: "inherit",
    background: theme.palette.type === "dark"
      ? "rgba(99, 102, 241, 0.15)"
      : "rgba(61, 92, 253, 0.08)",
    color: theme.palette.type === "dark" ? "#a5b4fc" : theme.palette.primary.main,
    transition: "all 0.15s",
    "&:hover": {
      background: theme.palette.type === "dark"
        ? "rgba(99, 102, 241, 0.25)"
        : "rgba(61, 92, 253, 0.15)",
    },
  },
  daysRow: {
    display: "flex",
    flexWrap: "wrap",
    gap: theme.spacing(0.75),
    marginBottom: theme.spacing(1.5),
  },
  dayChip: {
    fontWeight: 600,
    fontSize: "0.75rem",
    height: 30,
    cursor: "pointer",
    transition: "all 0.15s",
  },
  dayChipSelected: {
    background: theme.palette.type === "dark"
      ? "rgba(99, 102, 241, 0.85) !important"
      : `${theme.palette.primary.main} !important`,
    color: "#fff !important",
    border: "none !important",
  },
  dayChipSelectedClosed: {
    background: theme.palette.type === "dark"
      ? "rgba(239, 68, 68, 0.75) !important"
      : "#ef4444 !important",
    color: "#fff !important",
    border: "none !important",
  },
  dayChipUnselected: {
    background: "transparent !important",
    color: (theme.palette.type === "dark" ? "rgba(255,255,255,0.55)" : "rgba(0,0,0,0.45)") + " !important",
    border: theme.palette.type === "dark"
      ? "1px solid rgba(255,255,255,0.15) !important"
      : "1px solid rgba(0,0,0,0.15) !important",
  },
  dayChipDisabled: {
    opacity: "0.3 !important",
    cursor: "not-allowed !important",
    pointerEvents: "none",
  },
  timeRow: {
    display: "flex",
    alignItems: "flex-start",
    gap: theme.spacing(1.5),
  },
  timeField: {
    flex: 1,
    "& .MuiInputBase-root": {
      color: theme.palette.type === "dark" ? "rgba(255,255,255,0.9)" : "rgba(0,0,0,0.87)",
      background: theme.palette.type === "dark" ? "rgba(255,255,255,0.05)" : "transparent",
      borderRadius: 8,
    },
    "& .MuiInputLabel-root": {
      color: theme.palette.type === "dark" ? "rgba(255,255,255,0.45)" : "rgba(0,0,0,0.5)",
    },
    "& .MuiInputLabel-root.Mui-focused": {
      color: theme.palette.type === "dark" ? "#818cf8" : theme.palette.primary.main,
    },
    "& .MuiOutlinedInput-notchedOutline": {
      borderColor: theme.palette.type === "dark" ? "rgba(255,255,255,0.18)" : "rgba(0,0,0,0.2)",
    },
    "& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline": {
      borderColor: theme.palette.type === "dark" ? "rgba(255,255,255,0.35)" : "rgba(0,0,0,0.5)",
    },
    "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: theme.palette.type === "dark" ? "#818cf8" : theme.palette.primary.main,
    },
  },
  separator: {
    color: theme.palette.type === "dark" ? "rgba(255,255,255,0.3)" : "rgba(0,0,0,0.3)",
    fontWeight: 300,
    fontSize: "1.2rem",
    flexShrink: 0,
    marginTop: 12,
  },
  deleteBtn: {
    color: theme.palette.type === "dark" ? "rgba(255,255,255,0.3)" : "rgba(0,0,0,0.25)",
    marginTop: 4,
    "&:hover": {
      color: "#ef4444",
      background: "rgba(239, 68, 68, 0.08)",
    },
  },
  closedBadge: {
    flex: 1,
    padding: theme.spacing(1.2, 1.5),
    borderRadius: 8,
    background: theme.palette.type === "dark"
      ? "rgba(239, 68, 68, 0.08)"
      : "rgba(239, 68, 68, 0.06)",
    color: theme.palette.type === "dark" ? "#fca5a5" : "#b91c1c",
    fontSize: "0.85rem",
    fontWeight: 600,
    textAlign: "center",
  },
  addButton: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    padding: theme.spacing(1.2, 2.5),
    borderRadius: 10,
    border: theme.palette.type === "dark"
      ? "1.5px dashed rgba(255,255,255,0.2)"
      : "1.5px dashed rgba(0,0,0,0.18)",
    color: theme.palette.type === "dark" ? "rgba(255,255,255,0.45)" : "rgba(0,0,0,0.45)",
    cursor: "pointer",
    marginBottom: theme.spacing(2),
    background: "transparent",
    width: "100%",
    transition: "all 0.15s",
    "&:hover": {
      borderColor: theme.palette.type === "dark" ? "rgba(99,102,241,0.7)" : theme.palette.primary.main,
      color: theme.palette.type === "dark" ? "#818cf8" : theme.palette.primary.main,
      background: theme.palette.type === "dark" ? "rgba(99,102,241,0.06)" : "rgba(0,0,0,0.03)",
    },
  },
  addButtonText: {
    fontSize: "0.875rem",
    fontWeight: 500,
  },
  emptyHint: {
    textAlign: "center",
    color: theme.palette.type === "dark" ? "rgba(255,255,255,0.3)" : "rgba(0,0,0,0.3)",
    fontSize: "0.85rem",
    marginBottom: theme.spacing(2),
    padding: theme.spacing(3),
    border: theme.palette.type === "dark"
      ? "1px dashed rgba(255,255,255,0.1)"
      : "1px dashed rgba(0,0,0,0.1)",
    borderRadius: 12,
  },
  errorText: {
    color: "#ef4444",
    fontSize: "0.75rem",
    marginTop: theme.spacing(0.5),
  },
  timeFieldError: {
    "& .MuiOutlinedInput-notchedOutline": {
      borderColor: "#ef4444 !important",
    },
  },
  buttonContainer: {
    textAlign: "center",
  },
}));

function SchedulesForm({ initialValues, onSubmit, loading, labelSaveButton }) {
  const classes = useStyles();
  const [groups, setGroups] = useState([]);

  // Converte initialValues (array de 7 dias) → grupos por horário.
  // Dias com startTime/endTime vazios viram um grupo `isClosed: true`.
  useEffect(() => {
    if (!Array.isArray(initialValues) || initialValues.length === 0) return;
    const openMap = {};
    const closedDays = [];
    initialValues.forEach(d => {
      if (!d.startTime || !d.endTime) {
        if (d.weekdayEn) closedDays.push(d.weekdayEn);
        return;
      }
      const key = `${d.startTime}-${d.endTime}`;
      if (!openMap[key]) {
        openMap[key] = { id: makeId(), days: [], startTime: d.startTime, endTime: d.endTime, isClosed: false };
      }
      openMap[key].days.push(d.weekdayEn);
    });
    const parsed = Object.values(openMap);
    if (closedDays.length > 0) {
      parsed.push({ id: makeId(), days: closedDays, startTime: "", endTime: "", isClosed: true });
    }
    setGroups(parsed);
  }, [initialValues]);

  const usedDays = groups.flatMap(g => g.days);
  const availableDays = ALL_DAYS.filter(d => !usedDays.includes(d.weekdayEn));

  const handleToggleDay = (groupId, weekdayEn) => {
    setGroups(prev => prev.map(g => {
      if (g.id !== groupId) return g;
      const has = g.days.includes(weekdayEn);
      return { ...g, days: has ? g.days.filter(d => d !== weekdayEn) : [...g.days, weekdayEn] };
    }));
  };

  const handleTimeChange = (groupId, field, value) => {
    setGroups(prev => prev.map(g => g.id === groupId ? { ...g, [field]: value } : g));
  };

  const handleRemoveGroup = (groupId) => {
    setGroups(prev => prev.filter(g => g.id !== groupId));
  };

  const handleAddGroup = () => {
    setGroups(prev => [...prev, {
      id: makeId(),
      days: availableDays.map(d => d.weekdayEn),
      startTime: "",
      endTime: "",
      isClosed: false,
    }]);
  };

  const handleToggleClosed = (groupId) => {
    setGroups(prev => prev.map(g => {
      if (g.id !== groupId) return g;
      return { ...g, isClosed: !g.isClosed, startTime: "", endTime: "" };
    }));
  };

  // "Copiar para dias úteis": adiciona (sem remover de outros grupos) e
  // "rouba" dias já usados em outros grupos pra este.
  const handleApplyPreset = (groupId, daysPreset) => {
    setGroups(prev => prev.map(g => {
      if (g.id === groupId) return { ...g, days: daysPreset };
      return { ...g, days: g.days.filter(d => !daysPreset.includes(d)) };
    }));
  };

  // Validação por grupo: { [groupId]: "mensagem" | null }
  const groupErrors = useMemo(() => {
    const errors = {};
    groups.forEach(g => {
      if (g.isClosed) {
        errors[g.id] = g.days.length === 0 ? "Selecione pelo menos um dia" : null;
        return;
      }
      if (!g.startTime || !g.endTime) {
        errors[g.id] = "Preencha hora inicial e final";
        return;
      }
      const start = toMinutes(g.startTime);
      const end = toMinutes(g.endTime);
      if (start === null || end === null) {
        errors[g.id] = "Formato inválido (use HH:MM)";
        return;
      }
      if (start >= end) {
        errors[g.id] = "Hora final deve ser maior que hora inicial";
        return;
      }
      if (g.days.length === 0) {
        errors[g.id] = "Selecione pelo menos um dia";
        return;
      }
      errors[g.id] = null;
    });
    return errors;
  }, [groups]);

  const hasErrors = Object.values(groupErrors).some(Boolean);

  const handleSubmit = () => {
    if (hasErrors) return;
    const schedules = ALL_DAYS.map(d => {
      const group = groups.find(g => g.days.includes(d.weekdayEn));
      if (!group || group.isClosed) {
        return { weekday: d.weekday, weekdayEn: d.weekdayEn, startTime: "", endTime: "" };
      }
      return { weekday: d.weekday, weekdayEn: d.weekdayEn, startTime: group.startTime, endTime: group.endTime };
    });
    onSubmit(schedules);
  };

  return (
    <div className={classes.root}>
      {groups.length === 0 && (
        <div className={classes.emptyHint}>
          Nenhum horário configurado. Clique em "+ Adicionar horário" para começar.
        </div>
      )}

      {groups.map((group) => {
        const otherUsedDays = groups.filter(g => g.id !== group.id).flatMap(g => g.days);
        const error = groupErrors[group.id];
        const hasTimeError = !group.isClosed && group.startTime && group.endTime && (() => {
          const s = toMinutes(group.startTime);
          const e = toMinutes(group.endTime);
          return s === null || e === null || s >= e;
        })();

        return (
          <div
            key={group.id}
            className={`${classes.groupCard} ${group.isClosed ? classes.groupCardClosed : ""}`}
          >
            {/* Header: quick actions à esquerda + toggle fechado à direita */}
            <div className={classes.groupHeader}>
              <div className={classes.quickActions}>
                <button
                  type="button"
                  className={classes.quickActionBtn}
                  onClick={() => handleApplyPreset(group.id, WEEKDAYS)}
                  title="Segunda a sexta"
                >
                  Dias úteis
                </button>
                <button
                  type="button"
                  className={classes.quickActionBtn}
                  onClick={() => handleApplyPreset(group.id, ALL_WEEKDAYS_EN)}
                  title="Todos os dias da semana"
                >
                  Todos os dias
                </button>
              </div>
              <div className={classes.closedToggle}>
                <Typography className={classes.closedLabel}>Dia fechado</Typography>
                <Switch
                  size="small"
                  checked={group.isClosed}
                  onChange={() => handleToggleClosed(group.id)}
                  color="primary"
                />
              </div>
            </div>

            {/* Chips dos dias */}
            <div className={classes.daysRow}>
              {ALL_DAYS.map(d => {
                const isSelected = group.days.includes(d.weekdayEn);
                const isDisabled = otherUsedDays.includes(d.weekdayEn);
                const selectedClass = group.isClosed
                  ? classes.dayChipSelectedClosed
                  : classes.dayChipSelected;
                return (
                  <Chip
                    key={d.weekdayEn}
                    label={d.short}
                    size="small"
                    className={`${classes.dayChip} ${
                      isDisabled
                        ? classes.dayChipDisabled
                        : isSelected
                          ? selectedClass
                          : classes.dayChipUnselected
                    }`}
                    onClick={() => !isDisabled && handleToggleDay(group.id, d.weekdayEn)}
                  />
                );
              })}
            </div>

            {/* Campos de hora + botão remover, ou badge "Fechado" */}
            <div className={classes.timeRow}>
              {group.isClosed ? (
                <div className={classes.closedBadge}>
                  Sem atendimento nesse(s) dia(s)
                </div>
              ) : (
                <>
                  <NumberFormat
                    label="Hora Inicial"
                    value={group.startTime}
                    onValueChange={({ formattedValue }) => handleTimeChange(group.id, "startTime", formattedValue)}
                    variant="outlined"
                    margin="dense"
                    customInput={TextField}
                    format="##:##"
                    className={`${classes.timeField} ${hasTimeError ? classes.timeFieldError : ""}`}
                    placeholder="09:00"
                  />

                  <span className={classes.separator}>–</span>

                  <NumberFormat
                    label="Hora Final"
                    value={group.endTime}
                    onValueChange={({ formattedValue }) => handleTimeChange(group.id, "endTime", formattedValue)}
                    variant="outlined"
                    margin="dense"
                    customInput={TextField}
                    format="##:##"
                    className={`${classes.timeField} ${hasTimeError ? classes.timeFieldError : ""}`}
                    placeholder="18:00"
                  />
                </>
              )}

              <Tooltip title="Remover">
                <IconButton size="small" className={classes.deleteBtn} onClick={() => handleRemoveGroup(group.id)}>
                  <Trash2 size={18} />
                </IconButton>
              </Tooltip>
            </div>

            {error && <div className={classes.errorText}>{error}</div>}
          </div>
        );
      })}

      {/* Botão adicionar — só aparece quando há dias disponíveis */}
      {availableDays.length > 0 && (
        <button type="button" className={classes.addButton} onClick={handleAddGroup}>
          <Plus size={18} />
          <span className={classes.addButtonText}>Adicionar horário</span>
        </button>
      )}

      <div className={classes.buttonContainer}>
        <ButtonWithSpinner
          loading={loading}
          type="button"
          color="primary"
          variant="contained"
          onClick={handleSubmit}
          disabled={hasErrors}
        >
          {labelSaveButton ?? i18n.t("settings.schedules.form.save")}
        </ButtonWithSpinner>
      </div>
    </div>
  );
}

export default SchedulesForm;
